import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import { 
  Plus, Bell, Check, Calendar as CalendarIcon, User,
  Trash, EnvelopeSimple, Sparkle
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

import { API } from '../config/api';

const typeLabelsMap = {
  reminder: 'followUps.typeLabels.reminder',
  call: 'followUps.typeLabels.call',
  email: 'followUps.typeLabels.email',
  meeting: 'followUps.typeLabels.meeting'
};

export default function FollowUpsPage() {
  const { t, i18n } = useTranslation();
  const [followUps, setFollowUps] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    due_date: new Date(),
    message: '',
    type: 'reminder'
  });

  const fetchData = async () => {
    try {
      const [followUpsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/follow-ups?completed=${showCompleted}`),
        axios.get(`${API}/clients`)
      ]);
      setFollowUps(Array.isArray(followUpsRes.data) ? followUpsRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [showCompleted]);

  const openModal = () => {
    setFormData({
      client_id: '',
      due_date: new Date(),
      message: '',
      type: 'reminder'
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/follow-ups`, {
        ...formData,
        due_date: format(formData.due_date, 'yyyy-MM-dd')
      });
      setIsModalOpen(false);
      fetchData();
      toast.success(t('followUps.followUpCreated'));
    } catch (err) {
      console.error('Error creating follow-up:', err);
      toast.error(t('followUps.createError'));
    }
  };

  const toggleComplete = async (followUp) => {
    try {
      await axios.put(`${API}/follow-ups/${followUp.id}`, {
        completed: !followUp.completed
      });
      fetchData();
      toast.success(followUp.completed ? t('followUps.reopened') : t('followUps.completed'));
    } catch (err) {
      console.error('Error updating follow-up:', err);
    }
  };

  const handleDelete = async (followUpId) => {
    if (!window.confirm(t('followUps.deleteFollowUpConfirm'))) return;
    try {
      await axios.delete(`${API}/follow-ups/${followUpId}`);
      fetchData();
      toast.success(t('followUps.deleted'));
    } catch (err) {
      console.error('Error deleting follow-up:', err);
    }
  };

  const openEmailModal = async (followUp) => {
    setSelectedFollowUp(followUp);
    setEmailLoading(true);
    setIsEmailModalOpen(true);

    try {
      const client = (clients || []).find(c => c.id === followUp.client_id);
      const { data } = await axios.post(`${API}/ai/generate-email`, {
        client_name: client?.name || followUp.client_name || 'Cliente',
        quote_number: followUp.quote_id ? `(Rif: ${followUp.message})` : '',
        total: 0,
        items_summary: followUp.message || 'Sollecito preventivo serramenti',
        email_type: followUp.type === 'reminder' ? 'reminder' : 'followup'
      });
      setEmailContent(data.body || '');
    } catch (err) {
      console.error('AI error:', err);
      setEmailContent('Gentile Cliente,\n\nVolevamo verificare se ha avuto modo di esaminare il nostro preventivo.\n\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti');
    } finally {
      setEmailLoading(false);
    }
  };

  const sendEmail = async () => {
    const client = (clients || []).find(c => c.id === selectedFollowUp.client_id);
    if (!client?.email) {
      toast.error(t('followUps.clientNoEmail'));
      return;
    }

    try {
      await axios.post(`${API}/email/send`, {
        recipient_email: client.email,
        subject: 'Sollecito Preventivo - Infismart',
        html_content: `<div style="font-family: Arial, sans-serif; padding: 20px;">${emailContent.replace(/\n/g, '<br>')}</div>`
      });
      toast.success(t('followUps.emailSent'));
      setIsEmailModalOpen(false);
      toggleComplete(selectedFollowUp);
    } catch (err) {
      console.error('Email error:', err);
      toast.error(t('followUps.emailError'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'es' ? 'es-ES' : 'it-IT';
    return format(date, 'dd MMM yyyy', { locale: i18n.language === 'es' ? undefined : it });
  };

  const isOverdue = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="follow-ups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
            {t('followUps.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {t('followUps.manageDesc')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={showCompleted ? "default" : "outline"}
            onClick={() => setShowCompleted(!showCompleted)}
            className={showCompleted ? "bg-[#059669] hover:bg-[#047857]" : ""}
            data-testid="toggle-completed-button"
          >
            {showCompleted ? t('followUps.showOpen') : t('followUps.showCompleted')}
          </Button>
          <Button
            onClick={openModal}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
            data-testid="add-follow-up-button"
          >
            <Plus size={20} className="mr-2" />
            {t('followUps.new')}
          </Button>
        </div>
      </div>

      {/* Follow-ups List */}
      {(followUps || []).length === 0 ? (
        <div className="text-center py-12 bg-[#F4F6F8] rounded-sm border border-[#E2E8F0]">
          <Bell size={48} className="mx-auto text-[#4A5568] mb-4" />
          <h3 className="font-medium text-[#0A0F1A] mb-2">
            {showCompleted ? t('followUps.noCompletedFollowups') : t('followUps.noOpenFollowups')}
          </h3>
          <p className="text-sm text-[#4A5568] mb-4">
            {showCompleted ? t('followUps.completedAppearHere') : t('followUps.createFollowUpDesc')}
          </p>
          {!showCompleted && (
            <Button
              onClick={openModal}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
            >
              <Plus size={20} className="mr-2" />
              {t('followUps.createFollowUp')}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {(followUps || []).map((followUp) => (
            <div
              key={followUp.id}
              className={`border rounded-sm p-4 bg-white transition-all ${
                followUp.completed 
                  ? 'border-[#E2E8F0] opacity-60' 
                  : isOverdue(followUp.due_date)
                    ? 'border-red-300 bg-red-50'
                    : 'border-[#E2E8F0] hover:border-[#0033A0]'
              }`}
              data-testid={`follow-up-${followUp.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <button
                    onClick={() => toggleComplete(followUp)}
                    className={`mt-1 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
                      followUp.completed
                        ? 'bg-[#059669] border-[#059669] text-white'
                        : 'border-[#E2E8F0] hover:border-[#0033A0]'
                    }`}
                    data-testid={`toggle-complete-${followUp.id}`}
                  >
                    {followUp.completed && <Check size={12} weight="bold" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-[#4A5568]" />
                      <span className="font-medium text-[#0A0F1A]">{followUp.client_name}</span>
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${
                        followUp.type === 'email' ? 'bg-blue-100 text-blue-800' :
                        followUp.type === 'call' ? 'bg-green-100 text-green-800' :
                        followUp.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {t(typeLabelsMap[followUp.type])}
                      </span>
                    </div>
                    {followUp.message && (
                      <p className="text-sm text-[#4A5568] mb-2">{followUp.message}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs">
                      <CalendarIcon size={14} className={isOverdue(followUp.due_date) && !followUp.completed ? 'text-red-600' : 'text-[#4A5568]'} />
                      <span className={isOverdue(followUp.due_date) && !followUp.completed ? 'text-red-600 font-medium' : 'text-[#4A5568]'}>
                        {formatDate(followUp.due_date)}
                        {isOverdue(followUp.due_date) && !followUp.completed && ` (${t('followUps.overdue')})`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!followUp.completed && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEmailModal(followUp)}
                      className="text-[#0033A0] hover:bg-[#0033A0]/10"
                      data-testid={`email-button-${followUp.id}`}
                    >
                      <EnvelopeSimple size={18} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(followUp.id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`delete-follow-up-${followUp.id}`}
                  >
                    <Trash size={18} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('followUps.newFollowUp')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('followUps.clientRequired')}</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger className="mt-1" data-testid="follow-up-client-select">
                  <SelectValue placeholder={t('followUps.selectClientPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(clients || []).map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('followUps.typeLabel')}</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="mt-1" data-testid="follow-up-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(typeLabelsMap).map(([value, labelKey]) => (
                    <SelectItem key={value} value={value}>{t(labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('followUps.dueDateRequired')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full mt-1 justify-start text-left font-normal"
                    data-testid="follow-up-date-button"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.due_date, 'dd MMMM yyyy', { locale: it })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.due_date}
                    onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>{t('followUps.messageLabel')}</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="mt-1"
                rows={3}
                placeholder={t('followUps.addNote')}
                data-testid="follow-up-message-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                disabled={!formData.client_id}
                data-testid="save-follow-up-button"
              >
                {t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={isEmailModalOpen} onOpenChange={setIsEmailModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Sparkle className="text-[#D97706]" />
              {t('followUps.emailFollowUp')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {emailLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
              </div>
            ) : (
              <>
                <div className="text-sm text-[#4A5568]">
                  {t('followUps.recipientLabel')} {(clients || []).find(c => c.id === selectedFollowUp?.client_id)?.email || t('followUps.nd')}
                </div>
                <Textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="email-content-textarea"
                />
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={sendEmail}
                className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                disabled={emailLoading || !(clients || []).find(c => c.id === selectedFollowUp?.client_id)?.email}
                data-testid="send-email-button"
              >
                <EnvelopeSimple size={18} className="mr-2" />
                {t('followUps.sendEmail')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

